import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Copy, RefreshCw, ShieldCheck, Globe } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Button,
  Checkbox,
  Badge,
  OptionGridSelector,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  IconButton,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWaapAspect,
  WAAP_AREAS,
  useWaapAutoCert,
  useRequestWaapAutoCert,
  useWaapCertificates,
  useAttachWaapCertificate,
  useDistributeTigerCertToWaap,
  type WaapWebsiteInfo,
} from '@/api/waap';
import { useCerts, useIssueCert, useRecheckCert, type Cert, type CertStatus } from '@/api/tiger';
import { TigerCertPicker } from '@/pages/services/components/TigerCertPicker';
import { errMessage, certDnsNamesFor, waapCertCoversHost } from '@/pages/services/waap/waap-util';
import styles from './SslTab.module.css';

// Tiger cert statuses that are still resolving (poll while any is present).
const TRANSIENT: ReadonlySet<CertStatus> = new Set<CertStatus>(['awaiting_dns', 'issuing', 'pending']);

const MODES = [
  {
    value: 'managed',
    title: 'Managed SSL',
    description:
      'Free managed certificate from your inventory — issue once for this domain or reuse an existing one, attachable across every service; the private key stays server-side. Recommended.',
    icon: <ShieldCheck size={18} />,
  },
  {
    value: 'http',
    title: 'Standard Mode',
    description:
      'Issue an SSL certificate tied only to this service’s domain, validated through the delivery domain’s CNAME record. Best for a newly created delivery domain.',
    icon: <Globe size={18} />,
  },
];

/**
 * SSL Certificate — website-detail tab. Two certificate modes: Standard Mode
 * (Auto Let's Encrypt via the delivery-domain http challenge) and Managed SSL
 * (issue a free cert for this domain or reuse one from the Tiger inventory —
 * private key stays server-side). Website fixed by the detail route (`:domainId`).
 */
export function SslTab() {
  const { domainId } = useParams<{ domainId: string }>();
  const navigate = useNavigate();
  const { show } = useToast();

  // WAAP-native website info (incl. CNAME) — not the CDN domain-detail endpoint,
  // which fans out CDN aspect fetches that don't apply to a WAAP-only domain.
  const { data: domain } = useWaapAspect<WaapWebsiteInfo>(domainId, WAAP_AREAS.websiteInfo);
  const { data: cert, isLoading } = useWaapAutoCert(domainId);
  // Selected mode. Null until the user picks — the effective mode then defaults to
  // the ACTIVE cert's source (auto → Standard Mode, manual/none → Managed SSL) so
  // the selector reflects what's really provisioned. Derived below once cert loads.
  const [modeOverride, setModeOverride] = useState<string | null>(null);
  // When a cert is already active, the issue/replace flow is collapsed behind a
  // "Replace certificate" action so the page reads as "you're covered".
  const [replacing, setReplacing] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const request = useRequestWaapAutoCert(domainId ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Certificate requested' }),
    onError: (e) => show({ tone: 'danger', title: 'Request failed', description: errMessage(e) }),
  });

  const { data: certs } = useWaapCertificates(domainId);
  const [bundleId, setBundleId] = useState('');
  const [managedAgreed, setManagedAgreed] = useState(false);
  const attach = useAttachWaapCertificate(domainId ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Certificate attached' }),
    onError: (e) => show({ tone: 'danger', title: 'Attach failed', description: errMessage(e) }),
  });

  // Tiger (managed SSL inventory) — pick a reusable cert and distribute it here.
  const [pickerOpen, setPickerOpen] = useState(false);
  const distribute = useDistributeTigerCertToWaap(domainId ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Certificate attached from inventory' }),
    onError: (e) => show({ tone: 'danger', title: 'Attach failed', description: errMessage(e) }),
  });

  const name = domain?.domain ?? '';
  const cname = domain?.cname ?? '';

  // Issue a free Tiger cert for THIS domain inline — no round-trip to the cert
  // console. Owned-zone (shared wildcard) comes back active → auto-attach here;
  // a custom domain lands awaiting_dns and PendingTigerCerts surfaces the CNAME.
  const issue = useIssueCert({
    onSuccess: (c) => {
      if (c.status === 'active') {
        distribute.mutate({ cert_id: c.id });
      } else {
        show({
          tone: 'info',
          title: 'Certificate requested',
          description: 'Add the one-time DNS record shown below, then it attaches here.',
        });
      }
    },
    onError: (e) => show({ tone: 'danger', title: 'Issue failed', description: errMessage(e) }),
  });
  const issueThis = () => name && issue.mutate({ dns_names: certDnsNamesFor(name) });

  // A managed cert already awaiting verification for this domain must be completed
  // (or rechecked) before another can be issued — blocks re-issuing while pending.
  // Shares React Query's cache with PendingTigerCerts' identical query (no refetch).
  const { data: domainCerts = [] } = useCerts({ domain: name || undefined, limit: 50 });
  const hasPendingCert = useMemo(() => {
    const wildcard = name ? `*.${name}` : '';
    return domainCerts.some(
      (c) => TRANSIENT.has(c.status) && c.dns_names?.some((n) => n === name || n === wildcard),
    );
  }, [domainCerts, name]);

  // Managed Certificate select: only vendor-pool certs whose CN/SAN actually
  // covers this domain — otherwise we'd offer certs that can't attach here.
  const attachableCerts = useMemo(
    () => (certs?.list ?? []).filter((c) => waapCertCoversHost(c, name)),
    [certs, name],
  );

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  const status = cert?.status || 'none';
  const certType = cert?.cert_type || 'none';
  const hasCert = certType !== 'none';
  // cert_type reflects the SOURCE: `auto` = Standard Mode (Auto Let's Encrypt),
  // `manual` = Managed SSL (Tiger inventory / attached). Only a Standard-Mode cert
  // makes the Standard-Mode form read as "already requested".
  const leActive = certType === 'auto';
  const certSource = certType === 'auto' ? 'Standard Mode' : certType === 'manual' ? 'Managed SSL' : certType;
  // Reflect the active cert's source unless the user has picked a mode themselves.
  const mode = modeOverride ?? (certType === 'auto' ? 'http' : 'managed');
  const setMode = setModeOverride;
  // A cert is already provisioned → keep the issue/replace controls collapsed
  // until the user chooses to replace it (or one is mid-issue).
  const showIssueFlow = !hasCert || status === 'issuing' || replacing;

  return (
    <Section
      title="SSL Certificate"
      description="Request a free Auto Let's Encrypt certificate. Configure the CNAME record below at your DNS provider to complete ownership validation."
    >
      <div className={styles.stack}>
        <Card>
          <CardHeader>
            <CardTitle>Issue an SSL certificate</CardTitle>
            <CardDescription>
              {mode === 'managed'
                ? 'Recommended — a free managed certificate issued once and attachable across every service; the private key stays server-side.'
                : leActive
                  ? 'A Standard Mode certificate is already active. You can re-request or switch validation mode below.'
                  : 'Auto Let’s Encrypt via the delivery domain’s CNAME. Set the CNAME, accept the agreement, then request.'}
            </CardDescription>
          </CardHeader>
          <CardBody className={styles.bodyGrid}>
            {status === 'issuing' ? (
              <Alert tone="warning">
                A certificate is being issued for <strong>{name}</strong> — this can take a few minutes; the status
                updates automatically.
              </Alert>
            ) : hasCert ? (
              <Alert tone="success">
                A <strong>{certSource}</strong> certificate is active for <strong>{name}</strong>
                {cert?.request_time ? ` — issued ${new Date(cert.request_time).toLocaleString()}` : ''}.
              </Alert>
            ) : null}

            {showIssueFlow ? (
              <>
            <OptionGridSelector options={MODES} value={mode} onChange={setMode} aria-label="Certificate mode" />

            <div className={styles.modeContent}>
            {mode === 'http' ? (
              <>
                {/* Let's Encrypt option — record + agreement + action grouped in one panel. */}
                <div className={styles.pendingPanel}>
                  <CnameRecordRows host={name} target={cname || '<delivery-cname>'} />
                  <label className={styles.agreement}>
                    <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(Boolean(v))} />
                    <span>
                      <strong>VAS – SSL Agreement.</strong> SSL is a value-added service; requesting may take some time;
                      the Let's Encrypt certificate auto-renews and is free, subject to your service policies and Let's
                      Encrypt's.
                    </span>
                  </label>
                  <div className={styles.footer}>
                    <Button
                      variant={leActive ? 'secondary' : 'primary'}
                      disabled={!agreed || request.isPending}
                      onClick={() => request.mutate({ challenge_type: 'http' })}
                    >
                      {request.isPending
                        ? 'Requesting…'
                        : leActive
                          ? 'Re-request certificate'
                          : 'Request certificate'}
                    </Button>
                  </div>
                </div>

                {/* Second Standard-Mode option: attach a certificate you already
                    manage — only shown when at least one covers this domain. */}
                {attachableCerts.length > 0 && (
                  <div className={styles.pendingPanel}>
                    <div>
                      <div className={styles.altOptionTitle}>Or attach a certificate you manage</div>
                      <div className={styles.hint}>
                        Attach one of your account’s uploaded/managed certificates to this domain.
                      </div>
                    </div>
                    <Select value={bundleId} onValueChange={setBundleId}>
                      <SelectTrigger className={styles.certSelect}>
                        <SelectValue placeholder="Select certificate" />
                      </SelectTrigger>
                      <SelectContent>
                        {attachableCerts.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.commonName} ({c.validTo ? new Date(c.validTo).toLocaleDateString() : '—'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <label className={styles.agreement}>
                      <Checkbox checked={managedAgreed} onCheckedChange={(v) => setManagedAgreed(Boolean(v))} />
                      <span>
                        <strong>VAS – SSL Agreement.</strong> Bundling your SSL certificate to the selected domain is a
                        charged value-added service. Contact your account manager for CDN Wildcard / SAN subscriptions.
                      </span>
                    </label>
                    <div className={styles.footer}>
                      <Button
                        disabled={!bundleId || !managedAgreed || attach.isPending}
                        onClick={() => attach.mutate({ bundle_id: Number(bundleId) })}
                      >
                        {attach.isPending ? 'Attaching…' : 'Attach'}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {hasPendingCert ? (
                  // A cert is awaiting DNS: show its verification panel, with a
                  // quiet secondary link to use an existing cert instead.
                  <>
                    <PendingTigerCerts domain={name} />
                    <Button
                      variant="secondary"
                      className={styles.altLink}
                      startIcon={<ShieldCheck size={15} />}
                      disabled={distribute.isPending}
                      onClick={() => setPickerOpen(true)}
                    >
                      {distribute.isPending ? 'Attaching…' : 'Choose from inventory instead'}
                    </Button>
                  </>
                ) : (
                  <div className={styles.footer} style={{ gap: 'var(--vn-space-3)' }}>
                    <Button
                      variant="secondary"
                      startIcon={<ShieldCheck size={15} />}
                      disabled={!name || issue.isPending || distribute.isPending}
                      onClick={issueThis}
                    >
                      {issue.isPending ? 'Issuing…' : 'Issue free SSL for this domain'}
                    </Button>
                    <Button variant="ghost" disabled={distribute.isPending} onClick={() => setPickerOpen(true)}>
                      {distribute.isPending ? 'Attaching…' : 'Choose from inventory'}
                    </Button>
                  </div>
                )}
              </>
            )}
            </div>
              </>
            ) : (
              <div className={styles.footer} style={{ gap: 'var(--vn-space-3)' }}>
                <Button variant="ghost" onClick={() => navigate('/services/certinvent')}>
                  Manage certificates
                </Button>
                <Button variant="secondary" onClick={() => setReplacing(true)}>
                  Replace certificate
                </Button>
              </div>
            )}
          </CardBody>
        </Card>

      </div>

      <TigerCertPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        host={name}
        onSelect={(cert) => distribute.mutate({ cert_id: cert.id })}
        onIssue={name && !hasPendingCert ? () => { setPickerOpen(false); issueThis(); } : undefined}
        issuing={issue.isPending}
        issueLabel={`Issue free SSL for ${name}`}
      />
    </Section>
  );
}

/**
 * Surfaces any Tiger cert for THIS website's domain that is still resolving
 * (e.g. a wizard "skip & verify later" left it `awaiting_dns`). Shows the
 * one-time delegation CNAME + a Recheck button, and polls while transient so the
 * status flips to active on its own.
 */
function PendingTigerCerts({ domain }: { domain: string }) {
  const { show } = useToast();
  const { data: certs = [], refetch } = useCerts({ domain: domain || undefined, limit: 50 });

  // Match this website's domain: the exact host (what the wizard issues), or a
  // wildcard cert that happens to cover it (e.g. an imported *.domain).
  const wildcard = domain ? `*.${domain}` : '';
  const pending = useMemo(
    () =>
      certs.filter(
        (c) =>
          TRANSIENT.has(c.status) &&
          c.dns_names?.some((n) => n === domain || n === wildcard),
      ),
    [certs, domain, wildcard],
  );

  // Poll while any matched cert is still resolving.
  const hasTransient = pending.length > 0;
  useEffect(() => {
    if (!hasTransient) return;
    const t = setInterval(() => refetch(), 8000);
    return () => clearInterval(t);
  }, [hasTransient, refetch]);

  const recheck = useRecheckCert({
    onSuccess: () =>
      show({ tone: 'info', title: 'Checking…', description: 'Verifying the DNS record and issuing shortly.' }),
    onError: () => show({ tone: 'danger', title: 'Recheck failed' }),
  });

  if (!domain || pending.length === 0) return null;

  return (
    <div className={styles.bodyGrid}>
      <div>
        <div style={{ fontWeight: 'var(--vn-font-weight-semibold)', marginBottom: 2 }}>
          Pending certificate verification
        </div>
        <div className={styles.hint}>
          A free certificate was requested for this website but is still awaiting domain control. Add the one-time
          CNAME below at your DNS, then recheck.
        </div>
      </div>
      {pending.map((c: Cert) => (
          <div key={c.id} className={styles.pendingPanel}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
              <Badge tone="info" appearance="subtle" size="sm">
                {c.dns_names.join(', ')}
              </Badge>
              <Badge tone="warning" appearance="subtle" size="sm">
                {c.status === 'awaiting_dns' ? 'Awaiting DNS' : c.status}
              </Badge>
            </div>
            {c.delegation && (
              <CnameRecordRows host={c.delegation.cname_host} target={c.delegation.cname_target} />
            )}
            <div className={styles.footer}>
              <Button
                variant="secondary"
                startIcon={<RefreshCw size={15} />}
                disabled={recheck.isPending}
                onClick={() => recheck.mutate(c.id)}
              >
                {recheck.isPending ? 'Checking…' : 'Recheck'}
              </Button>
            </div>
          </div>
        ))}
    </div>
  );
}

/** CNAME delegation record: type badge + Host/Name + Value/Target copy rows.
 *  Shared by Standard Mode (delivery CNAME) and the pending verification panel
 *  so the two render byte-for-byte identically (same spacing, same markup). */
function CnameRecordRows({ host, target }: { host: string; target: string }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 2 }}>
        <Badge tone="primary" appearance="solid" size="sm">CNAME</Badge>
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          Add this record — not TXT — at your DNS provider
        </span>
      </div>
      <PendingCnameRow label="Host / Name" value={host} />
      <PendingCnameRow label="Value / Target" value={target} />
    </div>
  );
}

/** A copyable label/value line for a delegation CNAME record. */
function PendingCnameRow({ label, value }: { label: string; value: string }) {
  const { show } = useToast();
  const copy = () => {
    void navigator.clipboard.writeText(value);
    show({ tone: 'success', title: 'Copied' });
  };
  return (
    <div className={styles.cnameRow}>
      <span className={styles.cnameRowLabel}>{label}</span>
      <div className={styles.copyField}>
        <code className={styles.copyValue}>{value}</code>
        <IconButton variant="ghost" size="sm" aria-label={`Copy ${label}`} onClick={copy}>
          <Copy size={14} />
        </IconButton>
      </div>
    </div>
  );
}
